import React from 'react';
import SubPageTemplate from './SubPageTemplate';

const CookiePolicy: React.FC = () => {
  return (
    <SubPageTemplate title="Cookie Policy">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. What Are Cookies?</h2>
            <p className="text-gray-700 mb-6">
              Cookies are small text files that are placed on your computer or mobile device when you 
              visit a website. They are widely used to make websites work more efficiently and to provide 
              information to website owners about how users interact with their sites.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Cookies</h2>
            <p className="text-gray-700 mb-6">
              Book My Shoot uses cookies to enhance your browsing experience, analyze website traffic, 
              and provide personalized content. We use both session cookies (which expire when you close 
              your browser) and persistent cookies (which remain on your device for a set period).
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Essential Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are necessary for the website to function properly. They enable basic 
              functions like page navigation, access to secure areas, and form submissions.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Examples:</strong> Session management, security tokens, user authentication
              </p>
            </div>

            <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Performance Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies collect information about how visitors use our website, such as which 
              pages are visited most often and if users get error messages from web pages.
            </p>
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Examples:</strong> Google Analytics, website performance monitoring, error tracking
              </p>
            </div>

            <h3 className="text-xl font-medium text-gray-900 mb-3">3.3 Functionality Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies allow the website to remember choices you make and provide enhanced, 
              more personal features.
            </p>
            <div className="bg-yellow-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Examples:</strong> Language preferences, user interface customization, form data retention
              </p>
            </div>

            <h3 className="text-xl font-medium text-gray-900 mb-3">3.4 Marketing Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are used to track visitors across websites to display relevant and 
              engaging advertisements.
            </p>
            <div className="bg-purple-50 p-4 rounded-lg mb-6">
              <p className="text-gray-700 text-sm">
                <strong>Examples:</strong> Social media integration, advertising networks, remarketing pixels
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              We may use third-party services that set cookies on our website. These include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6">
              <li><strong>Google Analytics:</strong> Website traffic analysis and user behavior insights</li>
              <li><strong>Social Media Platforms:</strong> Facebook, Instagram, LinkedIn integration</li>
              <li><strong>Email Services:</strong> EmailJS for contact form submissions</li>
              <li><strong>Payment Processors:</strong> Secure payment processing cookies</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Cookie Duration</h2>
            
            <h3 className="text-xl font-medium text-gray-900 mb-3">5.1 Session Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are temporary and are deleted when you close your browser. They help 
              maintain your session while browsing our website.
            </p>

            <h3 className="text-xl font-medium text-gray-900 mb-3">5.2 Persistent Cookies</h3>
            <p className="text-gray-700 mb-6">
              These cookies remain on your device for a predetermined period (typically 30 days to 2 years) 
              and help us recognize you when you return to our website.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Managing Your Cookie Preferences</h2>
            
            <h3 className="text-xl font-medium text-gray-900 mb-3">6.1 Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              You can control and delete cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6">
              <li>View and delete cookies</li>
              <li>Block cookies from specific websites</li>
              <li>Block all cookies</li>
              <li>Set preferences for cookie acceptance</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-900 mb-3">6.2 Browser-Specific Instructions</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Chrome</h4>
                <p className="text-sm text-gray-700">
                  Settings → Privacy and security → Cookies and other site data
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Firefox</h4>
                <p className="text-sm text-gray-700">
                  Settings → Privacy & Security → Cookies and Site Data
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Safari</h4>
                <p className="text-sm text-gray-700">
                  Preferences → Privacy → Manage Website Data
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Edge</h4>
                <p className="text-sm text-gray-700">
                  Settings → Cookies and site permissions → Cookies and site data
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Impact of Disabling Cookies</h2>
            <p className="text-gray-700 mb-4">
              If you choose to disable cookies, some features of our website may not function properly:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-6">
              <li>Contact forms may not work correctly</li>
              <li>User preferences may not be saved</li>
              <li>Website performance may be affected</li>
              <li>Some interactive features may be unavailable</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookie Consent</h2>
            <p className="text-gray-700 mb-6">
              By continuing to use our website, you consent to our use of cookies as described in this 
              policy. You can withdraw your consent at any time by adjusting your browser settings or 
              contacting us directly.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Updates to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Cookie Policy from time to time to reflect changes in our practices 
              or for other operational, legal, or regulatory reasons. We will notify you of any material 
              changes by posting the updated policy on our website.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions about our use of cookies or this Cookie Policy, please contact us:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>Email:</strong> privacy@bookmyshot.com</p>
              <p className="text-gray-700 mb-2"><strong>Phone:</strong> +971 50 123 4567</p>
              <p className="text-gray-700"><strong>Address:</strong> Dubai, UAE</p>
            </div>
    </SubPageTemplate>
  );
};

export default CookiePolicy;
